import React, { Component, ReactNode } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import NavigationUtil from '../util/NavigationUtil';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import CustomTable from './custom/CustomTable';
import CustomPaginator, { CustomPaginatorProps } from '../components/custom/CustomPaginator';
import { ColumnProps } from 'primereact/column';
import ComponentName from '../constant/ComponentName';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import moment from 'moment';
import CustomLoading from './custom/CustomLoading';
import _ from 'lodash';
import { FormicaProps, withFormica } from '../helpers/withFormica';
import Subscription = subscribe.Subscription;
import Computation = subscribe.Computation;
import QueryBuilder = query.QueryBuilder;
import raw = query.raw;
import CaseClient from '../client/integration/case/CaseClient';
import { TransactionData, TransactionFailure } from '../domain/stream/transaction/TransactionDomains';
import CollectionNames from '../constant/CollectionNames';
import { CaseSettings } from '../domain/case/CaseDomains';
import { Link } from 'react-router-dom';

class ComponentErrorLog extends Component<Props, State> {
  public state: State = new State();

  private auditSubscription: Subscription | undefined;
  private caseSettingsSubscription: Subscription | undefined;
  private computation: Computation | undefined;

  private caseClient: CaseClient = GatewayContainer.getInstance().get(GatewayNames.CASE_CLIENT);

  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);

  public render(): ReactNode {
    const { failures, loading, rows, totalRecord, currentPage } = this.state;
    if (loading) {
      return <CustomLoading />;
    }
    if (failures.length == 0) {
      return (
        <div className={'flex flex-col max-h-[calc(100vh-21.25rem)] items-center justify-center overflow-y-scroll w-full h-full px-5 py-5'}>
          <div className={'font-semibold'}>No Audit Found</div>
        </div>
      );
    }
    return (
      <div className={'flex flex-col max-h-[calc(100vh-21.25rem)] overflow-y-scroll w-full h-full px-5 py-5'}>
        <CustomTable value={failures} columns={this.getColumns()} />
        <div className={'paginator'}>
          <CustomPaginator
            rows={rows}
            totalRecords={totalRecord}
            currentRecords={failures.length}
            rowsPerPageOptions={[5, 10, 20, 50, 100, 200]}
            onPageChange={this.onPageChange}
            currentPage={currentPage}
          />
        </div>
      </div>
    );
  }

  public async componentDidMount() {
    this.startTracker();
  }

  public componentWillUnmount() {
    this.stopTracker();
  }

  private startTracker = () => {
    const endpoint = `risk_transaction_log_${NavigationUtil.getTenant()}_*`;
    const query = this.getQuery();
    const caseSettingsQuery = QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).select('dataSource', 'name', 'keyField').build();
    this.auditSubscription = subscribe.Formica.subscribe(endpoint, query, undefined, 'elasticsearch');
    this.caseSettingsSubscription = subscribe.Formica.subscribe(CollectionNames.CASE_SETTINGS, caseSettingsQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.auditSubscription, this.caseSettingsSubscription]);
  };

  private stopTracker = async () => {
    await this.auditSubscription?.stop();
    this.computation?.stop();
  };

  private resubscribeTracker = async () => {
    if (this.computation == undefined) {
      this.startTracker();
    } else {
      this.computation.onStop(this.startTracker);
      await this.stopTracker();
    }
  };

  private tracker = () => {
    if (this.auditSubscription?.ready() && this.caseSettingsSubscription?.ready()) {
      this.buildData();
    }
  };

  private buildData = async () => {
    const endpoint = `risk_transaction_log_${NavigationUtil.getTenant()}_*`;
    const query = QueryBuilder.select()
      .from(endpoint)
      .orderBy({
        field: 'timestamp',
        operator: 'DESC',
      })
      .build();
    const count: number = await this.getCount();
    const data: TransactionData[] = await this.repository.findMany(query);
    const caseSettings = await this.repository.findMany<CaseSettings>(QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).build());
    const failures: TransactionFailureType[] = data
      .map((d: TransactionData) => {
        const failure = d.result.failures.find((f) => f.name == this.props.documentName && f.type == this.getType());
        if (failure) {
          return { failure, transaction: d };
        }
      })
      .filter((v) => v != undefined) as TransactionFailureType[];
    if (!_.isEqual(this.state.failures, failures)) {
      this.setState({ failures });
    }
    this.setState({ loading: false, totalRecord: count, caseSettings });
  };

  private getQuery = () => {
    const { currentPage, rows } = this.state;
    const endpoint = `risk_transaction_log_${NavigationUtil.getTenant()}_*`;
    return query.QueryBuilder.select()
      .from(endpoint)
      .skip((currentPage - 1) * rows)
      .limit(rows)
      .where(raw(this.getFilter() as any))
      .orderBy({
        field: 'createdAt',
        operator: 'DESC',
      })
      .build();
  };
  private onPageChange = (event: CustomPaginatorProps) => {
    this.setState({ rows: event.rows, currentPage: event.page, loading: true }, this.resubscribeTracker);
  };

  private getType = () => {
    const { component } = this.props;
    switch (component) {
      case ComponentName.VELOCITY:
        return 'VELOCITY';
      case ComponentName.BASIC_VARIABLE:
        return 'BASIC';
      case ComponentName.QUERY_VARIABLE:
        return 'QUERY';
      case ComponentName.LIST_VARIABLE:
        return 'LIST';
      case ComponentName.API_VARIABLE:
        return 'API';
      case ComponentName.STREAM:
        return 'STREAM';
      case ComponentName.RULE:
        return 'RULE';
    }
  };

  private getCount = (): Promise<number> => {
    const { component, documentName } = this.props;
    const endpoint = `risk_transaction_log_${NavigationUtil.getTenant()}_*`;
    const _query = query.QueryBuilder.select()
      .from(endpoint)
      .where(raw(this.getFilter() as any))
      .build();
    return this.caseClient.getErrorLogCount(_query, documentName, component);
  };

  private getColumns(): ColumnProps[] {
    return [
      {
        field: 'Id',
        header: 'Transaction Id',
        align: 'left',
        className: 'max-w-[100px] truncate',
        body: (record: TransactionFailureType) => {
          const url = this.calculateUrl(record.transaction);
          if (url == undefined) {
            return <div className='truncate'>{record.transaction.id}</div>;
          }
          return (
            <div className='truncate hover:text-primary'>
              <Link to={url} target='_blank' referrerPolicy='no-referrer' className='truncate'>
                {record.transaction.id}
              </Link>
            </div>
          );
        },
      },
      {
        field: 'Code',
        header: 'Code',
        body: (record: TransactionFailureType) => <div className={'flex items-center text-xs 2xl:text-md'}>{record.failure.code}</div>,
      },
      {
        field: 'Description',
        header: 'Description',
        body: (record: TransactionFailureType) => <div className={'flex items-center text-xs 2xl:text-md'}>{record.failure.description}</div>,
      },
      {
        field: 'timestamp',
        header: 'Timestamp',
        body: (record: TransactionFailureType) => (
          <div className={'flex items-center text-xs 2xl:text-md'}>{moment(record.transaction.createdAt).format('LLL')}</div>
        ),
      },
    ];
  }

  private getFilter = () => {
    const { documentName } = this.props;
    return {
      bool: {
        filter: [
          {
            nested: {
              path: 'result.failures',
              query: {
                bool: {
                  filter: [
                    {
                      bool: {
                        should: [
                          {
                            term: {
                              'result.failures.name.keyword': {
                                value: documentName,
                              },
                            },
                          },
                        ],
                        minimum_should_match: 1,
                      },
                    },
                    {
                      bool: {
                        should: [
                          {
                            term: {
                              'result.failures.type.keyword': {
                                value: this.getType(),
                              },
                            },
                          },
                        ],
                        minimum_should_match: 1,
                      },
                    },
                  ],
                },
              },
              score_mode: 'none',
            },
          },
        ],
      },
    };
  };

  private calculateUrl = (record: TransactionData) => {
    const { caseSettings } = this.state;
    const settings = caseSettings.find((c) => c.dataSource == record.name);
    if (settings == undefined) return;
    const urlParams = new URLSearchParams();
    urlParams.set('index', record.id);
    urlParams.set('domain', settings.name);
    urlParams.set('transaction', settings.dataSource);
    urlParams.set('keyField', settings.keyField);
    urlParams.set('keyValue', record.variables[settings.keyField]);
    return `/cases/${settings.name}/detail/${urlParams.toString()}`;
  };
}

interface Props extends FormicaProps {
  component: ComponentName;
  documentName: string;
}

class State {
  loading: boolean = true;
  caseSettings: CaseSettings[] = [];
  failures: TransactionFailureType[] = [];
  totalRecord: number = 0;
  rows: number = 20;
  currentPage: number = 1;
}

export type TransactionFailureType = { failure: TransactionFailure; transaction: TransactionData };

export default withFormica(ComponentErrorLog);
